import React, { useMemo, useState } from 'react';
import { Container, Row, Col, Card, Alert, Table, Badge, Button, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceDot, ReferenceArea,
} from 'recharts';
import { StatRow, StatCard } from '../components/insights/InsightPage';
import { chartColor, tooltipBox, useChartTheme } from '../components/chartTheme';
import { reportService } from '../services/apiService';
import { useInsightData } from '../hooks/useInsightData';
import { useIsMobile } from '../hooks/useMediaQuery';
import StackedTable from '../components/StackedTable';
import SortableHeader from '../components/SortableHeader';
import ReportHeader from '../components/ReportHeader';
import PlanFilter, { planKey, planLabel, planRank, planOptions as listPlans } from '../components/PlanFilter';
import { LoadingState, ErrorState } from '../components/PageState';
import { EMPTY, formatDate, formatTime, formatNumber } from '../utils/format';
import { rowClick } from '../utils/rows';
import { downloadCsv } from '../utils/csv';

/**
 * What share of new company sign-ups go on to pay, and how long they take. First payment dates
 * come from Stripe (via the backend); a company paying today with no Stripe record counts as
 * paying but sits out of the timing figures.
 */

const WINDOWS = [
  { value: 6, label: 'Last 6 months' },
  { value: 12, label: 'Last 12 months' },
  { value: 24, label: 'Last 24 months' },
  { value: 36, label: 'Last 36 months' },
  { value: 0, label: 'All time' },
];

/** Cohort chart bands, fastest first: one blue ramp in order, then a neutral for payers with no date. */
const BANDS = [
  { key: 'within30Days', label: 'Within 30 days', color: '--chart-ordinal-1' },
  { key: 'within31To90Days', label: '31 to 90 days', color: '--chart-ordinal-2' },
  { key: 'after90Days', label: 'After 90 days', color: '--chart-ordinal-3' },
  { key: 'undated', label: 'Paying, no date', color: '--chart-neutral' },
];

const MATURE_DAYS = 90;
const CURVE_TICKS = [0, 30, 60, 90, 180, 270, 365];
const CURVE_MARKERS = [30, 90];
const SEGMENT_GAP = 2;
const COMPANY_ROWS = 100;

const pct = (share, digits = 1) => (share == null ? EMPTY : `${(share * 100).toFixed(digits)}%`);
const ratio = (n, d) => (d ? n / d : null);
const days = (n) => (n == null ? EMPTY : `${n.toLocaleString('en-GB')} ${n === 1 ? 'day' : 'days'}`);
/** Percent axis ticks in even steps that clear the tallest value. */
const percentTicks = (value) => {
  const step = value <= 10 ? 2 : value <= 25 ? 5 : 10;
  const max = Math.max(step, Math.min(100, Math.ceil(value / step) * step));
  return Array.from({ length: max / step + 1 }, (_, i) => i * step);
};

/**
 * One stacked segment: a 2px surface gap separates it from the segment below (none on the
 * baseline), and only the top of each stack gets rounded corners.
 */
const segmentShape = (bandKey) => function Segment({ x, y, width, height, fill, payload }) {
  if (!height || height <= 0) return null;
  const present = BANDS.filter((b) => payload[b.key] > 0).map((b) => b.key);
  const h = present[0] === bandKey ? height : height - SEGMENT_GAP;
  if (h <= 0) return null;
  const r = present[present.length - 1] === bandKey ? Math.min(4, h, width / 2) : 0;
  const d = r > 0
    ? `M${x},${y + h} V${y + r} A${r},${r} 0 0 1 ${x + r},${y} H${x + width - r} A${r},${r} 0 0 1 ${x + width},${y + r} V${y + h} Z`
    : `M${x},${y} H${x + width} V${y + h} H${x} Z`;
  return <path d={d} fill={fill} />;
};

let measureContext;
/** Rendered width of chart text in the page font, so a label can be kept off the axis. */
const textWidth = (text, fontSize) => {
  measureContext = measureContext || document.createElement('canvas').getContext('2d');
  measureContext.font = `${fontSize}px ${getComputedStyle(document.body).fontFamily}`;
  return measureContext.measureText(text).width;
};

/**
 * Marker label above and to the left of its dot: the curve only rises, so that space is always clear of
 * the line. When that would run into the Y axis labels (an early marker on a narrow chart), it goes below
 * and to the right instead, which is under the line for the same reason.
 */
const MarkerLabel = ({ viewBox, text, plotLeft }) => {
  const fill = chartColor('--fg-default');
  if (viewBox.x - 2 - textWidth(text, 12) >= plotLeft) {
    return <text x={viewBox.x - 2} y={viewBox.y - 6} textAnchor="end" fill={fill} fontSize={12}>{text}</text>;
  }
  return <text x={viewBox.x + viewBox.width + 4} y={viewBox.y + viewBox.height + 14} textAnchor="start" fill={fill} fontSize={12}>{text}</text>;
};

/** The recharts legend sorts entries alphabetically, which scrambles the fastest-to-slowest order. */
const BandLegend = () => (
  <div className="d-flex flex-wrap gap-3 mb-2" style={{ fontSize: 13 }}>
    {BANDS.map((b) => (
      <span key={b.key} className="d-inline-flex align-items-center gap-2">
        <span style={{ width: 10, height: 10, borderRadius: 2, background: `var(${b.color})` }} />
        {b.label}
      </span>
    ))}
  </div>
);

const CurveTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div style={tooltipBox}>
      <div className="text-muted small mb-1">Day {point.day}</div>
      <div><strong>{point.share.toFixed(1)}%</strong> <span className="text-muted">had paid</span></div>
      <div className="text-muted small">{formatNumber(point.atRisk)} unpaid sign-ups still counted</div>
    </div>
  );
};

const CohortTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={tooltipBox}>
      <div className="mb-1"><strong>{row.label}</strong> <span className="text-muted">· {formatNumber(row.signups)} sign-ups</span></div>
      <div className="mb-1"><strong>{formatNumber(row.paid)} paid ({pct(ratio(row.paid, row.signups))})</strong></div>
      {[...BANDS].reverse().map((b) => (
        <div key={b.key} className="d-flex align-items-center gap-2">
          <span style={{ display: 'inline-block', width: 12, height: 2, background: chartColor(b.color) }} />
          <strong>{row.counts[b.key]}</strong>
          <span className="text-muted">{b.label}</span>
        </div>
      ))}
      {row.immature && <div className="text-muted small mt-1">Under {MATURE_DAYS} days old, still filling in</div>}
    </div>
  );
};

/** A company's payment status, as a sort rank (paying first) and the cell that shows it. */
const statusRank = (c) => {
  if (c.paymentDate === 'NONE') return 3;
  if (!c.payingNow) return 2;
  return c.paymentDate === 'UNDATED' ? 1 : 0;
};

const statusCell = (c) => {
  if (c.paymentDate === 'NONE') return <span className="text-muted">Never paid</span>;
  if (!c.payingNow) return <Badge bg="warning">Stopped paying</Badge>;
  if (c.paymentDate === 'UNDATED') return <Badge bg="secondary">Paying, no date</Badge>;
  return <Badge bg="success">Paying</Badge>;
};

/**
 * Companies table columns: the value each sorts by and which way the first click sorts. Missing
 * values (no payment date, no time to pay) sort last whichever way round.
 */
const COMPANY_SORTS = {
  name: { value: (c) => (c.name || c.companyId).toLowerCase(), first: 'asc' },
  plan: { value: (c) => planRank(planKey(c)), first: 'asc' },
  signedUpAt: { value: (c) => c.signedUpAt || null, first: 'desc' },
  firstPaidAt: { value: (c) => c.firstPaidAt || null, first: 'desc' },
  daysToPay: { value: (c) => c.daysToPay ?? null, first: 'asc' },
  status: { value: statusRank, first: 'asc' },
  paidUsers: { value: (c) => c.paidUsers, first: 'desc' },
  totalUsers: { value: (c) => c.totalUsers, first: 'desc' },
};

const compareCompanies = ({ field, direction }) => (a, b) => {
  const { value } = COMPANY_SORTS[field];
  const va = value(a);
  const vb = value(b);
  if (va == null || vb == null) return va == null && vb == null ? 0 : va == null ? 1 : -1;
  const order = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
  return (direction === 'asc' ? order : -order)
    || (a.name || a.companyId).localeCompare(b.name || b.companyId);
};

const SignupConversion = () => {
  const navigate = useNavigate();
  const [months, setMonths] = useState(12);
  const [grouping, setGrouping] = useState('month');
  const [companyFilter, setCompanyFilter] = useState('paying');
  const [search, setSearch] = useState('');
  const [hiddenPlans, setHiddenPlans] = useState(() => new Set());
  const [companySort, setCompanySort] = useState({ field: 'signedUpAt', direction: 'desc' });
  const [showAllCompanies, setShowAllCompanies] = useState(false);
  const isMobile = useIsMobile();
  const { data, loading, error, reload } = useInsightData(
    () => reportService.signupConversion({ months, grouping }),
    [months, grouping],
  );
  const t = useChartTheme();

  const curve = useMemo(() => (data?.curve || []).map((p) => ({ ...p, share: p.share * 100 })), [data]);

  const cohortRows = useMemo(() => (data?.cohorts || []).map((c) => {
    const paid = c.within30Days + c.within31To90Days + c.after90Days + c.undated;
    const row = { label: c.label, signups: c.signups, paid, immature: c.minAgeDays < MATURE_DAYS, counts: {} };
    BANDS.forEach((b) => {
      row.counts[b.key] = c[b.key];
      row[b.key] = c.signups ? (c[b.key] / c.signups) * 100 : 0;
    });
    return row;
  }), [data]);

  const planOptions = useMemo(() => listPlans(data?.companies || []), [data]);

  const companies = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.companies || []).filter((c) => {
      if (companyFilter === 'paying' && c.paymentDate === 'NONE') return false;
      if (companyFilter === 'notPaying' && c.paymentDate !== 'NONE') return false;
      if (hiddenPlans.has(planKey(c))) return false;
      return !q || (c.name || '').toLowerCase().includes(q);
    });
    return [...list].sort(compareCompanies(companySort));
  }, [data, companyFilter, search, hiddenPlans, companySort]);

  const sortCompanies = (field) => setCompanySort((prev) => (prev.field === field
    ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
    : { field, direction: COMPANY_SORTS[field].first }));

  // Exports the companies the table shows, in its order, all of them rather than the first page
  const exportCsv = () => {
    const isoDay = (value) => (value ? String(value).slice(0, 10) : '');
    downloadCsv(
      'signup-conversion',
      ['company_id', 'company_name', 'plan', 'enabled', 'signed_up', 'first_paid', 'days_to_pay', 'payment_date_source', 'paying_now', 'paid_users', 'total_users'],
      companies.map((c) => [
        c.companyId, c.name, c.accountType || '', c.enabled ? 'yes' : 'no',
        isoDay(c.signedUpAt), isoDay(c.firstPaidAt),
        c.daysToPay ?? '', c.paymentDate, c.payingNow ? 'yes' : 'no', c.paidUsers, c.totalUsers,
      ]),
    );
  };

  const s = data?.summary;
  const meta = data?.meta;
  const windowLabel = months === 0 ? 'all time' : `last ${months} months`;
  const accent = t.colors.emphasis;
  const surface = chartColor('--bg-default');
  const { muted } = t.colors;
  const maxDay = curve.length ? curve[curve.length - 1].day : 0;
  const curveTicks = percentTicks(Math.max(0, ...curve.map((p) => p.share)));
  const cohortTicks = percentTicks(Math.max(0, ...cohortRows.map((r) => BANDS.reduce((sum, b) => sum + r[b.key], 0))));
  const firstImmature = cohortRows.find((r) => r.immature);
  const lastCohort = cohortRows[cohortRows.length - 1];
  const periodWord = grouping === 'quarter' ? 'quarter' : 'month';
  const visibleCompanies = showAllCompanies ? companies : companies.slice(0, COMPANY_ROWS);
  const sortProps = { sortField: companySort.field, sortDirection: companySort.direction, onSort: sortCompanies };
  // Phones get shorter charts with a narrower axis, so the plot keeps most of the width
  const chartHeight = isMobile ? 260 : 340;
  const yAxisWidth = isMobile ? 40 : 48;
  const curveMargin = isMobile ? { top: 24, right: 12, left: 0, bottom: 24 } : { top: 24, right: 32, left: 8, bottom: 24 };
  const cohortMargin = isMobile ? { top: 24, right: 8, left: 0, bottom: 8 } : { top: 24, right: 16, left: 8, bottom: 8 };

  return (
    <Container fluid className="py-4">
      <ReportHeader
        title="Signup conversion"
        description="What share of new company sign-ups go on to pay, and how long they take to do it."
      />

      <Card className="shadow-sm mb-4">
        <Card.Body className="d-flex flex-wrap gap-3 align-items-end filter-bar">
          <Form.Group controlId="conversionWindow">
            <Form.Label className="small text-muted mb-1">Sign-ups from</Form.Label>
            <Form.Select size="sm" value={months} onChange={(e) => setMonths(Number(e.target.value))} style={{ width: isMobile ? '100%' : 'auto' }}>
              {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="conversionGrouping">
            <Form.Label className="small text-muted mb-1">Group by</Form.Label>
            <Form.Select size="sm" value={grouping} onChange={(e) => setGrouping(e.target.value)} style={{ width: isMobile ? '100%' : 'auto' }}>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
            </Form.Select>
          </Form.Group>
          {meta?.stripeStatus === 'OK' && (
            <div className="ms-auto small text-muted filter-bar-wide">Stripe data from {formatTime(meta.stripeFetchedAt)}, refreshed every 10 minutes</div>
          )}
        </Card.Body>
      </Card>

      {!error && meta?.stripeStatus === 'NOT_CONFIGURED' && (
        <Alert variant="warning">
          Stripe is not connected on this environment, so there are no payment dates: every payer shows as "Paying, no date" and the timing figures are empty.
          Set <code>STRIPE_API_KEY</code> to a restricted live key with read access to Subscriptions.
        </Alert>
      )}
      {!error && meta?.stripeStatus === 'FAILED' && (
        <Alert variant="warning">
          Could not read Stripe ({meta.stripeMessage || 'unknown error'}). The figures below use the database only, so payers have no dates and past payers who cancelled are missing.
        </Alert>
      )}

      {error ? (
        <Card className="shadow-sm"><ErrorState inCard message={error} onRetry={reload} /></Card>
      ) : !data ? (
        <Card className="shadow-sm"><LoadingState /></Card>
      ) : (
        <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity 120ms ease-out' }} aria-busy={loading}>
          <StatRow>
            <StatCard label="Sign-ups" value={formatNumber(s.signups)} sub={`Companies created, ${windowLabel}`} />
            <StatCard
              label="Became paying"
              value={pct(ratio(s.paying, s.signups))}
              sub={`${formatNumber(s.paying)} of ${formatNumber(s.signups)}, ${formatNumber(s.stillPaying)} still paying`}
              variant="primary"
            />
            <StatCard
              label="Paid within 30 days"
              value={pct(s.paidWithin30Days)}
              sub={s.paidWithin90Days == null ? 'Not enough sign-ups to say' : `${pct(s.paidWithin90Days)} within 90 days`}
            />
            <StatCard
              label="Median time to pay"
              value={days(s.medianDaysToPay)}
              sub={`Across ${formatNumber(s.dated)} ${s.dated === 1 ? 'payer' : 'payers'} with a Stripe date`}
            />
          </StatRow>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">How quickly sign-ups pay</h5>
              <small className="text-muted">
                Share of sign-ups that had paid by each day after signing up. Newer sign-ups count only for the days they have had, so they do not drag the line down.
              </small>
            </Card.Header>
            <Card.Body>
              {curve.length < 2 ? (
                <div className="text-center text-muted p-4">
                  Not enough sign-ups with a known outcome to draw the curve. It needs at least {meta.minAtRisk} and a payment date source.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <AreaChart data={curve} margin={curveMargin}>
                    <CartesianGrid {...t.grid} />
                    <XAxis
                      type="number"
                      dataKey="day"
                      {...t.xAxis}
                      domain={[0, maxDay]}
                      ticks={CURVE_TICKS.filter((d) => d <= maxDay)}
                      label={{ value: 'Days after signing up', position: 'insideBottom', offset: -16, fill: muted, fontSize: 12 }}
                    />
                    <YAxis
                      {...t.yAxis}
                      domain={[0, curveTicks[curveTicks.length - 1]]}
                      ticks={curveTicks}
                      tickFormatter={(v) => `${v}%`}
                      width={yAxisWidth}
                    />
                    <Tooltip {...t.tooltip} content={<CurveTooltip />} cursor={{ stroke: muted, strokeWidth: 1 }} />
                    <Area
                      type="stepAfter"
                      dataKey="share"
                      stroke={accent}
                      strokeWidth={2}
                      fill={accent}
                      fillOpacity={0.1}
                      dot={false}
                      activeDot={{ r: 5, fill: accent, stroke: surface, strokeWidth: 2 }}
                      isAnimationActive={false}
                    />
                    {CURVE_MARKERS.filter((d) => d <= maxDay).map((d) => (
                      <ReferenceDot
                        key={d}
                        x={d}
                        y={curve[d].share}
                        r={5}
                        fill={accent}
                        stroke={surface}
                        strokeWidth={2}
                        label={<MarkerLabel text={`${d} days: ${curve[d].share.toFixed(1)}%`} plotLeft={curveMargin.left + yAxisWidth} />}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Conversion by signup {periodWord}</h5>
              <small className="text-muted">
                Share of each {periodWord}'s sign-ups that have paid, split by how long they took. The shaded {periodWord}s are under {MATURE_DAYS} days old and still filling in.
              </small>
            </Card.Header>
            <Card.Body>
              <BandLegend />
              <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart data={cohortRows} margin={cohortMargin} barCategoryGap="20%">
                  <CartesianGrid {...t.grid} />
                  {firstImmature && (
                    <ReferenceArea
                      x1={firstImmature.label}
                      x2={lastCohort.label}
                      fill={chartColor('--bg-inset')}
                      fillOpacity={1}
                      label={{ value: 'Still filling in', position: 'insideTop', fill: muted, fontSize: 12, offset: -18 }}
                    />
                  )}
                  <XAxis dataKey="label" {...t.xAxis} interval="preserveStartEnd" minTickGap={16} />
                  <YAxis {...t.yAxis} domain={[0, cohortTicks[cohortTicks.length - 1]]} ticks={cohortTicks} tickFormatter={(v) => `${v}%`} width={yAxisWidth} />
                  <Tooltip {...t.tooltip} content={<CohortTooltip />} />
                  {BANDS.map((b) => (
                    <Bar
                      key={b.key}
                      dataKey={b.key}
                      name={b.label}
                      stackId="paid"
                      fill={chartColor(b.color)}
                      maxBarSize={24}
                      shape={segmentShape(b.key)}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light"><h5 className="mb-0">Cohorts</h5></Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0 align-middle" style={{ fontVariantNumeric: 'tabular-nums' }}>
                <thead className="table-light">
                  <tr>
                    <th>{grouping === 'quarter' ? 'Quarter' : 'Month'}</th>
                    <th className="text-end">Sign-ups</th>
                    <th className="text-end d-none d-md-table-cell">Within 30 days</th>
                    <th className="text-end d-none d-md-table-cell">31 to 90 days</th>
                    <th className="text-end d-none d-md-table-cell">After 90 days</th>
                    <th className="text-end d-none d-md-table-cell">Paying, no date</th>
                    <th className="text-end">Paid so far</th>
                    <th className="text-end d-none d-md-table-cell">Still paying</th>
                    <th className="text-end">Median time to pay</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.cohorts].reverse().map((c) => {
                    const paid = c.within30Days + c.within31To90Days + c.after90Days + c.undated;
                    const cell = (n) => (
                      <td className="text-end d-none d-md-table-cell">
                        {formatNumber(n)}
                        {c.signups > 0 && <span className="text-muted small ms-1">{pct(ratio(n, c.signups), 0)}</span>}
                      </td>
                    );
                    return (
                      <tr key={c.start}>
                        <td>
                          {c.label}
                          {c.minAgeDays < MATURE_DAYS && <span className="text-muted small ms-2">filling in</span>}
                        </td>
                        <td className="text-end">{formatNumber(c.signups)}</td>
                        {cell(c.within30Days)}
                        {cell(c.within31To90Days)}
                        {cell(c.after90Days)}
                        {cell(c.undated)}
                        <td className="text-end">
                          <strong>{formatNumber(paid)}</strong>
                          {c.signups > 0 && <span className="text-muted small ms-1">{pct(ratio(paid, c.signups), 0)}</span>}
                        </td>
                        <td className="text-end d-none d-md-table-cell">{formatNumber(c.stillPaying)}</td>
                        <td className="text-end">{days(c.medianDaysToPay)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <Row className="g-2 align-items-center">
                <Col md="auto"><h5 className="mb-0 me-2">Companies</h5></Col>
                <Col md={3}>
                  <Form.Control size="sm" type="search" placeholder="Search company" aria-label="Search company" value={search} onChange={(e) => setSearch(e.target.value)} />
                </Col>
                <Col xs={6} md="auto">
                  <Form.Select size="sm" value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} aria-label="Payment">
                    <option value="paying">Paid at some point</option>
                    <option value="notPaying">Never paid</option>
                    <option value="all">All sign-ups</option>
                  </Form.Select>
                </Col>
                <Col xs={6} md="auto">
                  <PlanFilter id="conversionPlans" options={planOptions} hidden={hiddenPlans} onChange={setHiddenPlans} />
                </Col>
                <Col className="d-flex align-items-center justify-content-md-end flex-wrap gap-2 text-muted small">
                  {formatNumber(companies.length)} of {formatNumber(data.companies.length)} sign-ups
                  <Button variant="outline-secondary" size="sm" onClick={exportCsv} disabled={!companies.length}>
                    <Download size={14} className="me-1" aria-hidden="true" />Export CSV
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              {companies.length === 0 ? (
                <div className="p-4 text-center text-muted">No sign-ups match these filters</div>
              ) : (
                <StackedTable responsive hover className="mb-0 align-middle" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <thead className="table-light">
                    <tr>
                      <SortableHeader field="name" {...sortProps}>Company</SortableHeader>
                      <SortableHeader field="plan" {...sortProps}>Plan</SortableHeader>
                      <SortableHeader field="signedUpAt" {...sortProps}>Signed up</SortableHeader>
                      <SortableHeader field="firstPaidAt" align="d-none d-md-table-cell" {...sortProps}>First paid</SortableHeader>
                      <SortableHeader field="daysToPay" align="text-end" {...sortProps}>Time to pay</SortableHeader>
                      <SortableHeader field="status" {...sortProps}>Status</SortableHeader>
                      <SortableHeader field="paidUsers" align="text-end d-none d-md-table-cell" {...sortProps}>Paid users</SortableHeader>
                      <SortableHeader field="totalUsers" align="text-end d-none d-md-table-cell" {...sortProps}>Users</SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCompanies.map((c) => (
                      <tr key={c.companyId} onClick={rowClick(() => navigate(`/companies/${c.companyId}`))} style={{ cursor: 'pointer' }}>
                        <td>
                          <Link className="cell-link" to={`/companies/${c.companyId}`}>{c.name || c.companyId}</Link>
                          {!c.enabled && <Badge bg="secondary" className="ms-2">Disabled</Badge>}
                        </td>
                        <td>{planLabel(planKey(c))}</td>
                        <td>{formatDate(c.signedUpAt)}</td>
                        <td className="d-none d-md-table-cell">{formatDate(c.firstPaidAt)}</td>
                        <td className="text-end">{days(c.daysToPay)}</td>
                        <td>{statusCell(c)}</td>
                        <td className="text-end d-none d-md-table-cell">{formatNumber(c.paidUsers)}</td>
                        <td className="text-end d-none d-md-table-cell">{formatNumber(c.totalUsers)}</td>
                      </tr>
                    ))}
                  </tbody>
                </StackedTable>
              )}
            </Card.Body>
            {companies.length > COMPANY_ROWS && (
              <Card.Footer className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <small className="text-muted">
                  Showing {formatNumber(visibleCompanies.length)} of {formatNumber(companies.length)}
                </small>
                <Button variant="link" size="sm" onClick={() => setShowAllCompanies(!showAllCompanies)}>
                  {showAllCompanies ? `Show first ${COMPANY_ROWS}` : `Show all ${formatNumber(companies.length)}`}
                </Button>
              </Card.Footer>
            )}
          </Card>

          <Card className="shadow-sm">
            <Card.Body className="small text-muted">
              <p className="mb-2">
                <strong style={{ color: 'var(--fg-default)' }}>How this is worked out.</strong> A sign-up is a company created in the window.
                The first payment date is when Stripe first charged one of the company's subscriptions, cancelled subscriptions included,
                so a company that paid and later stopped still counts as having converted.
              </p>
              <p className="mb-2">
                A company paying today with no Stripe record (usually invoiced by hand) counts as paying, but we do not know when it started,
                so it is left out of the curve, the 30 and 90 day figures and the median.
              </p>
              <p className="mb-0">
                The curve stops once fewer than {meta.minAtRisk} unpaid sign-ups are old enough to count, because past that point a single payment moves it by whole points.
                {meta.unmatchedStripeCustomers > 0 && ` ${formatNumber(meta.unmatchedStripeCustomers)} Stripe ${meta.unmatchedStripeCustomers === 1 ? 'customer has' : 'customers have'} charges but no matching company, so they are not counted.`}
              </p>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default SignupConversion;
